import { useState } from "react";
import {
  useAronWorkbenchStore,
  type AronDropZone,
} from "../../store/aronWorkbenchStore";
import { hasTabDrag, markTabDropHandled, readTabDrag } from "./tabDnd";

/** How far in from an edge still counts as "split on this side". */
const EDGE = 0.25;

function zoneAt(e: React.DragEvent, el: HTMLElement): AronDropZone {
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return "center";
  const fx = (e.clientX - r.left) / r.width;
  const fy = (e.clientY - r.top) / r.height;
  const edges: [AronDropZone, number][] = [
    ["left", fx],
    ["right", 1 - fx],
    ["top", fy],
    ["bottom", 1 - fy],
  ];
  const [zone, distance] = edges.reduce((a, b) => (b[1] < a[1] ? b : a));
  return distance < EDGE ? zone : "center";
}

const ZONE_BOX: Record<AronDropZone, string> = {
  center: "inset-0",
  left: "inset-y-0 left-0 w-1/2",
  right: "inset-y-0 right-0 w-1/2",
  top: "inset-x-0 top-0 h-1/2",
  bottom: "inset-x-0 bottom-0 h-1/2",
};

const ZONE_LABEL: Record<AronDropZone, string> = {
  center: "Przenieś rozmowę do tej grupy",
  left: "Podziel — rozmowa po lewej",
  right: "Podziel — rozmowa po prawej",
  top: "Podziel — rozmowa u góry",
  bottom: "Podziel — rozmowa na dole",
};

/**
 * The VS Code drop zones over a group's body: drag a tab here and the half of
 * the pane that would become the new group lights up — release on an edge to
 * split, in the middle to just move the conversation into this group.
 *
 * Rendered only while a tab is actually being dragged, so it never sits over the
 * conversation swallowing clicks.
 */
export function AronPaneDropOverlay({ groupId }: { groupId: string }) {
  const dragging = useAronWorkbenchStore((s) => s.draggingTab);
  const dropTabOnGroup = useAronWorkbenchStore((s) => s.dropTabOnGroup);
  const setDraggingTab = useAronWorkbenchStore((s) => s.setDraggingTab);
  const [zone, setZone] = useState<AronDropZone | null>(null);

  if (!dragging) return null;

  return (
    <div
      className="absolute inset-0 z-30"
      onDragOver={(e) => {
        if (!hasTabDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setZone(zoneAt(e, e.currentTarget));
      }}
      onDragLeave={() => setZone(null)}
      onDrop={(e) => {
        const p = readTabDrag(e);
        const target = zone ?? zoneAt(e, e.currentTarget);
        setZone(null);
        // The dragged tab may be re-parented by this very drop, so its dragend
        // would never fire — clear the drag here or the overlay stays up and
        // eats every click on the conversation.
        setDraggingTab(null);
        if (!p) return;
        e.preventDefault();
        markTabDropHandled();
        dropTabOnGroup(p.groupId, p.key, groupId, target);
      }}
    >
      {zone && (
        <div
          className={`pointer-events-none absolute flex items-center justify-center border-2 border-red-500 bg-red-500/15 ${ZONE_BOX[zone]}`}
        >
          <span className="bg-red-600 px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-white">
            {ZONE_LABEL[zone]}
          </span>
        </div>
      )}
    </div>
  );
}
